import { cn } from "../ui/cn";

/**
 * An editable table cell: whatever it renders, with a real `<select>` laid
 * transparently over the top.
 *
 * A native select rather than a custom popover, because everything that makes
 * a picker good is already implemented in it -- keyboard navigation, type-ahead,
 * staying on screen near the viewport edge, and behaving properly on touch.
 * Hiding it over a custom face keeps the table's visual density while giving up
 * none of that.
 *
 * Click and key events are stopped here: the row underneath opens the ticket,
 * and reassigning something must never also navigate away from the list you are
 * working down.
 */
export function InlineSelect({
  value,
  options,
  onChange,
  label,
  children,
  className,
}: {
  /** Current value; "" represents null. */
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
  /** Accessible name -- the visible face is not a label the select can use. */
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "group/edit relative flex min-w-0 items-center gap-1 rounded py-0.5",
        "hover:bg-surface-2",
        className,
      )}
    >
      <span className="min-w-0 truncate">{children}</span>

      {/* Only on hover: a permanent caret on every row would be eight columns
          of visual noise for an action taken on one row at a time. */}
      <span
        aria-hidden
        className="shrink-0 text-[8px] leading-none text-faint opacity-0 transition-opacity group-hover/edit:opacity-100"
      >
        ▼
      </span>

      <select
        aria-label={label}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => event.stopPropagation()}
        className="absolute inset-0 w-full cursor-pointer opacity-0"
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
}
